import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Organization } from '@ee/common/models';
import { OrganizationService } from '@ee/common/services';
import { STATES } from '@ee/common/constants';
import { Observable, of, Subscription } from 'rxjs';
import { IntakeEvictionService } from './services/intake-eviction.service';
import { LandingPageVisit } from './models/landing-page-visit.model';
import { STEPPER_GLOBAL_OPTIONS } from '@angular/cdk/stepper';
import { environment } from '../environments/environment';
import { BreakpointObserver, Breakpoints, BreakpointState } from '@angular/cdk/layout';
import { AnalyticsService } from '@ee/common/analytics';

@Component({
  selector: 'ee-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  providers: [
    {
      provide: STEPPER_GLOBAL_OPTIONS,
      useValue: { displayDefaultIndicatorType: false }
    }
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
  public attorney: Organization;
  public logo$: Observable<string | undefined> = of(undefined);
  public smallScreen = false;
  states = STATES;
  year = new Date().getFullYear();
  isLoading = true;
  isSuccess = false;
  subs: Subscription[] = [];
  landingPageVisit: LandingPageVisit;
  baseUrl = environment.base_url;

  constructor(
    private organizationService: OrganizationService,
    private intakeEvictionService: IntakeEvictionService,
    private breakpointObserver: BreakpointObserver,
    private analyticsService: AnalyticsService
  ) {}

  ngOnInit() {
    this.analyticsService.init(environment.posthogKey, {
      api_host: environment.posthogHost,
      ui_host: environment.posthogUiHost,
      capture_exceptions: true
    });

    const subdomain = location.hostname.split('.').shift();

    // calculate screen size
    this.subs.push(
      this.breakpointObserver
        .observe([Breakpoints.Small, Breakpoints.HandsetPortrait])
        .subscribe((state: BreakpointState) => {
          this.smallScreen = state.matches;
        })
    );

    this.subs.push(
      this.organizationService.getAttorneyByIdentifier(subdomain).subscribe({
        next: (attorney: Organization) => {
          if (attorney && attorney.eligible_for_intake_page) {
            this.attorney = attorney;
            this.analyticsService.capture('intake_landing_viewed', {
              attorney_id: attorney.id,
              attorney_name: attorney.company_name,
              subdomain
            });
            this.subs.push(
              this.intakeEvictionService
                .addLandingPageVisit(this.attorney.id)
                .subscribe((landingPageVisit: LandingPageVisit) => {
                  this.landingPageVisit = landingPageVisit;
                })
            );
            if (attorney.has_logo) {
              this.logo$ = this.organizationService.generateAttorneyLogoUrl(attorney.subdomain);
            }
          }
          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;
          this.attorney = null;
        }
      })
    );
  }

  ngOnDestroy() {
    this.subs.forEach((s) => s.unsubscribe());
  }

  evictionSaved() {
    this.intakeEvictionService.updatePageLoadVisits(this.landingPageVisit);
    this.analyticsService.capture('intake_form_submitted', {
      attorney_id: this.attorney?.id,
      attorney_name: this.attorney?.company_name
    });
    this.isSuccess = true;
  }
}
